<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components/widgets/Avatar.vue';
import BrandIcon from 'dashboard/components/BrandIcon.vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
  showAction: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['detail']);
const { t } = useI18n();
const isRTL = useMapGetter('accounts/isRTL');

// Sửa username để lấy từ name thay vì agent
const username = computed(() => props.agent?.name || '');
const avatarSize = 48;

// Sửa statusConf để lấy từ availability thay vì status
const statusConf = computed(() => {
  const value = (props.agent?.availability || '').toLowerCase();
  const map = {
    online: {
      text: 'Online',
      cls: 'text-green-600 bg-[rgba(18,183,106,0.08)]',
      dot: 'bg-green-500',
    },
    offline: {
      text: 'Offline',
      cls: 'text-gray-500 bg-[rgba(37,43,55,0.08)]',
      dot: 'bg-gray-400',
    },
  };
  return map[value] || { text: value || '—', cls: 'text-gray-500 bg-transparent', dot: 'bg-transparent' };
});

function onDetail() {
  emit('detail', props.agent?.id);
}
</script>

<template>
  <div
    class="rounded-lg bg-white dark:border dark:border-n-vars-black-bd/80 dark:bg-n-vars-black-br/30 p-3 w-full text-n-gray-3"
    :class="{ 'text-right': isRTL }"
  >
    <!-- Header -->
    <div class="mb-3">
      <div class="text-sm text-n-gray-5 mb-2">
        Nhà cung cấp
      </div>
      <div class="flex items-center gap-3">
        <div class="relative flex-shrink-0 w-8 h-8">
          <template v-if="agent.thumbnail">
            <img
              :src="agent.thumbnail"
              alt="avatar"
              class="w-8 h-8 rounded-full object-cover block"
              draggable="false"
            />
          </template>
          <template v-else>
            <Avatar :username="username" class="w-8 h-8 rounded-full" />
          </template>     
          <!-- Messenger Icon -->
          <div
            class="absolute bottom-0 right-0 w-[13px] h-[13px] rounded-full overflow-hidden"
          >
            <BrandIcon name="messenger" :size="14" />
          </div>
        </div>

        <div class="flex-1 min-w-0">
          <h6 class="overflow-hidden text-sm font-medium leading-[1.2] whitespace-nowrap text-ellipsis">
            {{ agent.name }}
          </h6>
          <span class="text-xs text-n-gray-5 whitespace-nowrap text-ellipsis block">
            {{ agent.email }}
          </span>
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="space-y-3">
      <div class="flex justify-between text-sm">
        <span class="text-n-gray-5">Mở</span>
        <span class="text-n-gray-7 dark:text-n-gray-3 font-medium">{{ agent.metric?.open ?? 0 }}</span>
      </div>
      <div class="flex justify-between text-sm">
        <span class="text-n-gray-5">Trạng thái</span>
        <span class="px-2 py-0.5 rounded text-xs" :class="statusConf.cls">
          {{ statusConf.text }}
        </span>
      </div>
      <div class="flex justify-between text-sm">
        <span class="text-n-gray-5">Không giám sát</span>
        <span class="text-n-gray-7 dark:text-n-gray-3 font-medium">{{ agent.metric?.unattended ?? 0 }}</span>
      </div>
    </div>

    <!-- Action -->
    <div v-if="showAction" class="mt-4 text-sm flex items-center justify-between">
        <span class="text-n-gray-5">Chi tiết tổng đài viên</span>
        <button
        class="px-3 py-[6px] rounded-lg text-xs text-n-gray-7 dark:text-n-gray-3 bg-[#12B76A0D] dark:bg-[#7176800D] hover:bg-green-500 transition-colors"
        @click="onDetail"
        >
        Chi tiết
      </button>
    </div>
  </div>
</template>